import * as React from 'react';
import { FC, useCallback, useMemo, useState } from 'react';

import { Box, FormikSubmitButton } from '@rbilabs/universal-components';
import { Field, FieldProps, Formik } from 'formik';
import { useIntl } from 'react-intl';
import { Keyboard } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';
import { superstruct } from 'superstruct';

import { Accordion, AccordionItem } from 'components/accordion';
import { ActionButtonSizes, ActionButtonVariants, ActionLink } from 'components/action-button';
import { CreateSupportCaseFeedback } from 'components/create-support-case-feedback';
import { FormikCheckbox } from 'components/formik-V2/checkbox';
import { FormikMaskInput } from 'components/formik-V2/mask-input';
import { FormikSelect } from 'components/formik-V2/select';
import { FormikTextInput } from 'components/formik-V2/text-input';
import { FormikDateInput } from 'components/formik/date-input';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { ALERT_STYLE, SupportAlertBox } from 'components/support-alert-box';
import { KeyboardAwareView } from 'components/ucl/keyboard-aware-view';
import { TermsOfServiceLabel } from 'components/user-info-form/checkbox-labels';
import { IRestaurantFragment, useGetRestaurantsQuery } from 'generated/sanity-graphql';
import { simplifiedServiceModesForSupport } from 'pages/support-v2/constants';
import { serializeServiceMode } from 'state/amplitude/utils';
import { CreateCaseState, ISupportFormData } from 'state/knowledge-force/types';
import { useScrollContext } from 'state/scroll';
import {
  getFormErrorsForStruct,
  isEmailValid,
  requiredBoolean,
  requiredString,
  requiredValidDateObject,
} from 'utils/form';
import noop from 'utils/noop';
import { routes } from 'utils/routing';

import { StoreSelectorModal } from '../store-selector-modal';
import {
  ButtonWrap,
  ContentWrap,
  MainWrap,
  NeedHelpHeading,
  PageHeading,
  PageSubHeading,
  SectionHeading,
  TitleWrap,
} from '../support-form-v2.styled';

import { IFaq, ISupportV2FormViewProps } from './types';

const getAddress = (store: IRestaurantFragment) => {
  const addressFields = ['address1', 'address2', 'city', 'state', 'country', 'zip'];
  const address = store.physicalAddress ?? {};
  return addressFields
    .map(field => address[field])
    .filter(Boolean)
    .join(', ');
};

export const SupportV2FormView: FC<React.PropsWithChildren<ISupportV2FormViewProps>> = ({
  alertMessage,
  bigTitle,
  caseReferenceNumber,
  createCaseError,
  createCaseState,
  faqs,
  handleSupportFormSubmit,
  initialValues,
  onFaqInteraction = noop,
  orderDate,
  serviceMode,
  smallTitle,
  useHighVolumeSupportMessage,
  showRestaurantSelector,
  showServiceModeSelector,
}) => {
  const { formatDate, formatMessage, formatTime } = useIntl();
  const { scrollRef } = useScrollContext();

  const errorMessageByType = useMemo(
    () => ({
      requiredString: formatMessage({ id: 'requiredField' }),
      requiredBoolean: formatMessage({ id: 'requiredField' }),
      requiredValidDateObject: formatMessage({ id: 'requiredDate' }),
      optionalString: 'this should never render, but still needs to be part of the struct',
    }),
    [formatMessage]
  );

  const struct = useMemo(
    () =>
      superstruct({
        types: {
          requiredBoolean,
          requiredString,
          requiredValidDateObject,
          optionalString: (value: string) => !value || value.length > 0,
        },
      }),
    []
  );

  const valueValidationObject = useMemo(
    () =>
      struct({
        comments: 'requiredString',
        email: 'requiredString',
        issueId: 'optionalString',
        issueSubCategory: 'requiredString',
        name: 'requiredString',
        orderDate: 'optionalString',
        agreesToTermsOfService: 'requiredBoolean',
        serviceMode: 'optionalString',
        ...(!!showRestaurantSelector && {
          storeId: 'requiredString',
          storeAddress: 'requiredString',
          orderDateTime: 'requiredValidDateObject',
        }),
      }),
    [showRestaurantSelector, struct]
  );

  const getFormErrors = getFormErrorsForStruct<ISupportFormData, typeof errorMessageByType>(
    valueValidationObject,
    errorMessageByType
  );

  const [isRestaurantSelectorShowing, setIsRestaurantSelectorShowing] = useState(false);
  const { refetch: fetchRestaurants } = useGetRestaurantsQuery({ skip: true });

  const fetchAndSetStoreDetailsToForm = useCallback(
    async (storeId: string, setField: (field: string, value: string) => void) => {
      const { data: stores } = await fetchRestaurants({ filter: { _id: storeId }, limit: 1 });
      const newStore = stores?.allRestaurants?.[0];
      if (newStore) {
        const newAddress = getAddress(newStore);
        setField('storeId', newStore.number ?? '');
        setField('storeAddress', newAddress);
      }
    },
    [fetchRestaurants]
  );

  const validateForm = (values: ISupportFormData) => {
    const invalidFormatEmailError: { email?: string; agreesToTermsOfService?: string } = {};
    if (!isEmailValid(values.email)) {
      invalidFormatEmailError.email = formatMessage({ id: 'notValidEmailError' });
    }
    if (!values.agreesToTermsOfService) {
      invalidFormatEmailError.agreesToTermsOfService = formatMessage({ id: 'requiredField' });
    }
    return { ...invalidFormatEmailError, ...getFormErrors(values) };
  };

  const serviceModeOptions = simplifiedServiceModesForSupport.map((mode, key) => ({
    index: key,
    value: formatMessage({ id: mode }),
    label: formatMessage({ id: mode }),
  }));
  const noServiceModeFromOrder = serviceMode === serializeServiceMode(null);

  const handleOpenRestaurantSelection = () => {
    const isOpen = Keyboard.isVisible();
    const KEYBOARD_HIDE_TIME_EST = 300; // Estimated time for keyboard to hide (in milliseconds)

    KeyboardController.dismiss();
    // Delay opening the modal until the keyboard is fully hidden.
    // This is necessary because opening a modal while the keyboard is active
    // can cause layout glitches due to the keyboard's animation not completing.
    // See: https://github.com/kirillzyusko/react-native-keyboard-controller/issues/387
    setTimeout(
      () => {
        setIsRestaurantSelectorShowing(true);
      },
      isOpen ? KEYBOARD_HIDE_TIME_EST : 0
    );
  };

  return (
    <KeyboardAwareView
      withScroll
      ref={ref => (scrollRef.current = ref)}
      keyboardShouldPersistTaps="handled"
    >
      <ButtonWrap>
        <ActionLink
          size={ActionButtonSizes.SMALL}
          to={routes.supportCategories}
          variant={ActionButtonVariants.OUTLINE}
        >
          {formatMessage({ id: 'backToTopics' })}
        </ActionLink>
      </ButtonWrap>
      <MainWrap>
        <TitleWrap>
          <PageHeading>{smallTitle}</PageHeading>
          <PageSubHeading>{bigTitle}</PageSubHeading>
        </TitleWrap>

        {alertMessage && (
          <SupportAlertBox alertStyle={ALERT_STYLE.error} testId="ineligible-for-refund">
            {alertMessage}
          </SupportAlertBox>
        )}
      </MainWrap>

      {faqs.length > 0 && (
        <>
          <SectionHeading>{formatMessage({ id: 'faq' })}</SectionHeading>
          <Accordion>
            {faqs.map((faq: IFaq, i) => (
              <AccordionItem
                activeHighlight={true}
                title={faq.title}
                key={i}
                onInteract={(isExpanded, isFirstInteraction) =>
                  onFaqInteraction(isExpanded, isFirstInteraction, faq)
                }
              >
                <SanityBlockRenderer content={faq.content as any} />
              </AccordionItem>
            ))}
          </Accordion>
        </>
      )}

      <ContentWrap>
        <NeedHelpHeading>{formatMessage({ id: 'needMoreHelpSendUsAMessage' })}</NeedHelpHeading>

        <Formik
          initialValues={initialValues}
          onSubmit={handleSupportFormSubmit}
          validate={validateForm}
          enableReinitialize
          validateOnMount
        >
          {({ setFieldValue, values }) => {
            return (
              <MainWrap>
                <Field
                  name="name"
                  id="name"
                  value={values.name}
                  component={FormikTextInput}
                  label={formatMessage({ id: 'name' })}
                  autoFocus
                  testID="name-input"
                />
                {orderDate && (
                  <Field
                    name="orderDate"
                    component={FormikTextInput}
                    testID="order-date-field"
                    label={formatMessage({ id: 'orderDate' })}
                    value={`${formatTime(orderDate)} ${formatDate(orderDate)}`}
                    isDisabled
                  />
                )}
                <Field
                  name="email"
                  component={FormikTextInput}
                  value={values.email}
                  label={formatMessage({ id: 'yourEmail' })}
                  isDisabled={initialValues.email}
                  testID="email-input"
                />
                {showRestaurantSelector && (
                  <>
                    <Field name="storeAddress">
                      {(props: FieldProps) => (
                        <FormikMaskInput
                          {...props}
                          label={formatMessage({ id: 'location' })}
                          onPress={handleOpenRestaurantSelection}
                          placeholder={formatMessage({ id: 'clickToSelectLocation' })}
                          value={values.storeAddress}
                        />
                      )}
                    </Field>

                    <Field name="orderDateTime">
                      {(props: FieldProps) => {
                        return (
                          <Box margin="2">
                            <FormikDateInput
                              {...props}
                              day={values.orderDateTime?.day || ''}
                              month={values.orderDateTime?.month || ''}
                              year={values.orderDateTime?.year || ''}
                              testID="order-date-time-input"
                              label={formatMessage({ id: 'orderDate' })}
                              onChange={(inputName, inputText) => {
                                setFieldValue(`orderDateTime.${inputName}`, inputText);
                              }}
                            />
                          </Box>
                        );
                      }}
                    </Field>
                  </>
                )}
                {isRestaurantSelectorShowing && (
                  <StoreSelectorModal
                    onDismiss={() => setIsRestaurantSelectorShowing(false)}
                    onStoreSelect={(storeId: string) =>
                      fetchAndSetStoreDetailsToForm(storeId, setFieldValue)
                    }
                  />
                )}
                <Field
                  name="comments"
                  testID="comments-input"
                  component={FormikTextInput}
                  multiline
                  textAlignVertical="center"
                  label={formatMessage({
                    id: showRestaurantSelector
                      ? 'describeIssueAndIncludePhoneNumber'
                      : 'describeIssue',
                  })}
                  required
                />
                {noServiceModeFromOrder && showServiceModeSelector && (
                  <Field
                    name="serviceMode"
                    testID="service-mode-dropdown"
                    component={FormikSelect}
                    label={formatMessage({ id: 'pleaseSelectOne' })}
                    options={serviceModeOptions}
                    required
                  />
                )}
                <Field
                  aria-label={formatMessage({ id: 'agreeToTermsOfService' })}
                  name="agreesToTermsOfService"
                  testID="faq-support-tos"
                >
                  {(props: FieldProps) => (
                    <FormikCheckbox
                      {...props}
                      alignItems="flex-start"
                      label={<TermsOfServiceLabel />}
                    />
                  )}
                </Field>

                <Box>
                  <FormikSubmitButton
                    testID="submit-button"
                    isLoading={createCaseState === CreateCaseState.LOADING}
                    width="full"
                    disabled={!!caseReferenceNumber}
                  >
                    {formatMessage({ id: 'submit' })}
                  </FormikSubmitButton>
                </Box>
              </MainWrap>
            );
          }}
        </Formik>

        <CreateSupportCaseFeedback
          createCaseState={createCaseState}
          createCaseErrorMessage={createCaseError}
          useHighVolumeSupportMessage={useHighVolumeSupportMessage}
        />
      </ContentWrap>
    </KeyboardAwareView>
  );
};
